import { getChannelHost } from 'src/utils/messages/singleton'
import { installModalForwarder } from 'src/utils/messages/modalChannel'

// Single document-level click listener that handles every
// <dialog class="widget-modal"> on the page (including ones added
// after this script runs — there's no upfront DOM scan). Each modal
// partial just renders its dialog markup with an optional
// iframe[data-src] inside; this script provides the runtime
// behavior uniformly:
//
//   - Open via [data-modal-target="<modal-id>"] on any element. Lazy
//     iframe load on first open (data-src → src) so we don't pay
//     the network cost when the donor never opens the modal. Each
//     iframe is registered with the channel host at src-load time
//     so its CONNECT handshake from inside completes — see below.
//   - Close via [data-modal-close] on any element inside the dialog,
//     or by clicking the dialog's backdrop (a click whose target is
//     the dialog element itself, since the visible content sits in
//     an inner div).
//
// (Scroll-lock is no longer done here — it's the CSS rule
// `html:has(.widget-modal[open])` in donate_v7.css; see showModal.)
//
// Modal-content iframes (served by Widgets::ModalsController) live
// at the same origin and send channel messages back to this window
// — e.g. the gift-aid iframe's "I agree" button sends
// `giftAidConfirmed` (handled by useGiftAid) and `closeModal`
// (handled below). We are their channel host: when an iframe's src
// is set we call `host.register(iframe)` so its client-side CONNECT
// handshake completes. register() is idempotent so the call is
// safe to repeat on every open.
//
// Parent-frame delegation for modals lives on the openModal pipeline
// (src/widgets/embed/openModal.js + openModalTriggers.js) — triggers
// declare data-plg-modal-* attrs and the dispatcher decides whether
// to render locally, channel-send to a parent, or fall through to
// the embed-script default.

const host = getChannelHost({ origin: location.origin })

// Forwarder for the opener ↔ modal channel — catches `toOpener`
// envelopes from openModal-rendered modal iframes that landed here
// (mode 1 top-frame donate widget, or mode 3 channel-failed
// in-iframe fallback). 'close' closes the dialog; other types are
// no-ops here because in mode 1/3 the opener IS this window —
// useGiftAid + other onModalMessage subscribers catch the inner
// type via host.onAll('toOpener', …) directly.
installModalForwarder(host)

host.onAll('closeModal', ({ id }) => {
  const modal = document.getElementById(id)
  if (modal) modal.close()
})

const showModal = id => {
  const modal = document.getElementById(id)
  if (!modal) return console.error(`Modal not found: ${id}`)

  const iframe = modal.querySelector('iframe[data-src]')
  if (iframe) {
    iframe.src ||= iframe.dataset.src
    host.register(iframe)
  }

  // Scroll-lock is CSS — `html:has(.widget-modal[open])` in
  // donate_v7.css — so there's nothing to toggle here; just open.
  modal.showModal()
}

const hideModal = close => close.closest('dialog').close()

document.addEventListener('click', e => {
  const modalShow = e.target.closest('[data-modal-target]')
  if (modalShow) return showModal(modalShow.dataset.modalTarget)

  const modalClose = e.target.closest('[data-modal-close]')
  if (modalClose) return hideModal(modalClose)

  if (e.target.matches('.widget-modal')) e.target.close()
})
